import { trackGoogleAnalyticsEvent, trackGoogleAnalyticsPageView } from "./googleAnalytics.js";
import { trackPixelAnalyticsEvent, trackPixelPageView } from "./pixelAnalytics.js";
import { getWebAnalyticsDeviceContext } from "./deviceContext.js";
import { getMallTrackingChannel } from "./trackingChannel.js";

const QUEUE_KEY = "mall_event_sdk_queue";
const DEVICE_ID_KEY = "mall_event_sdk_device_id";
const SESSION_KEY = "mall_event_sdk_session";
const FIRST_TOUCH_KEY = "mall_event_sdk_first_touch";
const LAST_ROUTE_KEY = "mall_event_sdk_last_route";

const SESSION_TTL = 30 * 60 * 1000;
const MAX_QUEUE_SIZE = 500;
const MAX_BATCH_SIZE = 20;
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const BASE_RETRY_MS = 5 * 1000;
const MAX_RETRY_MS = 5 * 60 * 1000;
const UPLOAD_TIMEOUT_MS = 15 * 1000;
const REQUEST_FAILURE_DEDUP_WINDOW_MS = 15 * 1000;
const SCREEN_VIEW_DEDUP_WINDOW_MS = 500;
const API_REQUEST_FAIL_EVENT_CODE = "api_request_fail";
const REQUEST_FAILURE_IGNORE_PATTERNS = ["/mallEventLog/batchSave"];
const COMPONENT_MANAGED_VIEW_ROUTE_PREFIXES = ["/tb/goods", "/wd/goods", "/alibaba/goods"];

// P0 表格字段名是上报口径。保留业务侧已有旧字段，同时在 source 中补齐这些标准字段。
// 时长、搜索、低代码和第三方认证事件不在本轮处理范围内。
export const P0_EVENT_PARAM_SCHEMA = {
  home_page_view: { userId: "string", sessionId: "string", utmSource: "string", device: "string" },
  home_recommend_product_click: { productId: "string", productName: "string", price: "number", rank: "number", source: "string" },
  product_detail_view: { productId: "string", productName: "string", price: "number", fromPage: "string", sourcePlatform: "string", shopId: "string" },
  add_to_cart_click: { productId: "string", productName: "string", spec: "string", quantity: "number", price: "number", isSuccess: "boolean", failReason: "string" },
  buy_now_click: { productId: "string", productName: "string", spec: "string", quantity: "number", price: "number", isSuccess: "boolean", failReason: "string" },
  cart_page_view: { itemCount: "number", checkedCount: "number", totalAmount: "number" },
  cart_checkout_click: { itemCount: "number", goodsAmount: "number", discountAmount: "number", payAmount: "number", isSuccess: "boolean" },
  expert_agent_page_view: { userId: "string", fromPage: "string" },
  expert_agent_submit_click: { productUrl: "string", sourcePlatform: "string", remark: "string", isSuccess: "boolean" },
  confirm_order_page_view: { "Product ID+Product Link": "string", "Total number of products": "number", "Source platform (Taobao: N; 1688: M)": "string" },
  confirm_order_submit_click: { "Product ID+Product Link": "string", "Total number of products": "number", "Source platform (Taobao: N; 1688: M)": "string", isSuccess: "boolean", "Page source": "string" },
  domestic_order_pay_page_view: { orderId: "string", orderType: "string", payAmount: "number" },
  domestic_order_pay_method_select: { orderId: "string", payMethodId: "string", payMethodName: "string" },
  domestic_order_confirm_pay_click: { orderId: "string", payAmount: "number", payMethod: "string", isSuccess: "boolean" },
  domestic_order_pay_success: { orderId: "string", payAmount: "number", payMethod: "string" },
  domestic_order_pay_fail: { orderId: "string", payAmount: "number", payMethod: "string", failReason: "string", errorCode: "string" },
  payment_success_recommend_product_click: { productId: "string", rank: "number" },
  domestic_order_list_view: { currentTab: "string", userId: "string" },
  domestic_order_cancel_click: { orderId: "string", orderStatus: "string", cancelReason: "string" },
  domestic_order_pay_click: { orderId: "string", payAmount: "number" },
  purchase_order_detail_view: { orderId: "string", orderStatus: "string", orderAmount: "number", itemCount: "number" },
  purchase_order_cancel_click: { orderId: "string", orderStatus: "string", cancelReason: "string" },
  purchase_order_pay_click: { orderId: "string", payAmount: "number" },
  warehouse_page_view: { userId: "string", packageCount: "number", pendingCount: "number", "Unstored shipmentCount": "number" },
  apply_consolidation_click: { packageIds: "string", packageCount: "number", totalWeight: "number", isSuccess: "boolean" },
  consolidation_modal_view: { packageCount: "number", totalWeight: "number", totalVolume: "number" },
  consolidation_submit_click: { packageCount: "number", totalWeight: "number", destination: "string", shippingMethod: "string", estimatedFee: "number", isSuccess: "boolean" },
  consolidation_order_submit_fail: { failReason: "string", errorCode: "string", packageCount: "number" },
  consolidation_order_pay_page_view: { consolidationId: "string", shippingFee: "number" },
  consolidation_order_pay_method_select: { payMethodId: "string", payMethodName: "string" },
  consolidation_order_confirm_pay_click: { consolidationId: "string", payAmount: "number", payMethod: "string", isSuccess: "boolean" },
  consolidation_order_pay_success: { consolidationId: "string", payAmount: "number", payMethod: "string" },
  consolidation_order_pay_fail: { consolidationId: "string", payAmount: "number", payMethod: "string", failReason: "string" },
  consolidation_order_list_view: { currentTab: "string", packageCount: "number", userId: "string" },
  consolidation_pay_click: { consolidationId: "string", payAmount: "number" },
  consolidation_detail_view: { consolidationId: "string", status: "string", shippingMethod: "string", shippingFee: "number", weight: "number", destination: "string" },
  consolidation_detail_cancel_click: { consolidationId: "string", status: "string", isSuccess: "boolean", failReason: "string" },
  consolidation_detail_pay_click: { consolidationId: "string", payAmount: "number" },
  my_order_entry_click: { orderStatus: "string" },
  my_warehouse_entry_click: { pendingCount: "number" },
  my_package_entry_click: { shippingCount: "number" },
  address_save_submit: { addressId: "string", region: "string", country: "string", isDefault: "boolean", isSuccess: "boolean", failReason: "string" },
  login_page_view: { fromPage: "string", redirectUrl: "string" },
  login_with_password_click: { accountType: "string", isSuccess: "boolean", failReason: "string" },
  login_with_email_code_click: { emailDomain: "string", isSuccess: "boolean", failReason: "string" },
  register_page_view: { utmSource: "string", inviteCode: "string" },
  register_submit_click: { registerType: "string", isSuccess: "boolean", failReason: "string", inviteCode: "string" },
};

const EVENT_CODE_MAP = {
  web_init: "web_init",
  low_code_interaction: "low_code_interaction",
  click_home_register_coupon_banner: "click_home_register_coupon_banner",

  // 首页访问（表格英文名：HomePageView）。
  home_page_view: "home_page_view",
  // 首页推荐商品点击（表格英文名：HomeRecommendProductClick）。
  home_recommend_product_click: "home_recommend_product_click",

  // 商品详情页访问（表格英文名：ProductDetailView）。
  product_detail_view: "product_detail_view",
  // 加入购物车点击（表格英文名：AddToCartClick）。
  add_to_cart_click: "add_to_cart_click",
  // 立即购买点击（表格英文名：BuyNowClick）。
  buy_now_click: "buy_now_click",

  // 购物车页访问（表格英文名：CartPageView）。
  cart_page_view: "cart_page_view",
  // 去结算点击（表格英文名：CartCheckoutClick）。
  cart_checkout_click: "cart_checkout_click",

  // 专家代购页访问（表格英文名：ExpertAgentPageView）。
  expert_agent_page_view: "expert_agent_page_view",
  // 专家代购提单点击（表格英文名：ExpertAgentSubmitClick）。
  expert_agent_submit_click: "expert_agent_submit_click",

  // 确认订单页访问（表格英文名：Confirm order page traffic）。
  confirm_order_page_view: "confirm_order_page_view",
  // 确认订单提交点击（表格英文名：Confirm Order Page - Click the Confirm button）。
  confirm_order_submit_click: "confirm_order_submit_click",
  // 国内订单支付页访问（表格英文名：DomesticOrderPayPageView）。
  domestic_order_pay_page_view: "domestic_order_pay_page_view",
  // 国内订单支付方式选择（表格英文名：DomesticOrderPayMethodSelect）。
  domestic_order_pay_method_select: "domestic_order_pay_method_select",
  // 国内订单确认支付点击（表格英文名：DomesticOrderConfirmPayClick）。
  domestic_order_confirm_pay_click: "domestic_order_confirm_pay_click",
  // 国内订单支付成功（表格英文名：DomesticOrderPaySuccess）。
  domestic_order_pay_success: "domestic_order_pay_success",
  // 国内订单支付失败（表格英文名：DomesticOrderPayFail）。
  domestic_order_pay_fail: "domestic_order_pay_fail",
  // 支付成功页推荐商品点击（表格英文名：Payment success page - Product recommendation click）。
  payment_success_recommend_product_click: "payment_success_recommend_product_click",

  // 国内订单列表页访问（表格英文名：DomesticOrderListView）。
  domestic_order_list_view: "domestic_order_list_view",
  // 国内订单取消点击（表格英文名：DomesticOrderCancelClick）。
  domestic_order_cancel_click: "domestic_order_cancel_click",
  // 国内订单付款点击（表格英文名：DomesticOrderPayClick）。
  domestic_order_pay_click: "domestic_order_pay_click",

  // 采购订单详情页访问（表格英文名：PurchaseOrderDetailView）。
  purchase_order_detail_view: "purchase_order_detail_view",
  // 采购订单取消点击（表格英文名：PurchaseOrderCancelClick）。
  purchase_order_cancel_click: "purchase_order_cancel_click",
  // 采购订单付款点击（表格英文名：PurchaseOrderPayClick）。
  purchase_order_pay_click: "purchase_order_pay_click",

  // 我的仓库页访问（表格英文名：WarehousePageView）。
  warehouse_page_view: "warehouse_page_view",
  // 申请集运点击（表格英文名：ApplyConsolidationClick）。
  apply_consolidation_click: "apply_consolidation_click",

  // 发起集运弹窗访问（表格英文名：ConsolidationModalView）。
  consolidation_modal_view: "consolidation_modal_view",
  // 提交集运订单点击（表格英文名：ConsolidationSubmitClick）。
  consolidation_submit_click: "consolidation_submit_click",
  // 集运订单提交失败（表格英文名：ConsolidationOrderSubmitFail）。
  consolidation_order_submit_fail: "consolidation_order_submit_fail",
  // 集运订单支付页访问（表格英文名：ConsolidationOrderPayPageView）。
  consolidation_order_pay_page_view: "consolidation_order_pay_page_view",
  // 集运订单支付方式选择（表格英文名：ConsolidationOrderPayMethodSelect）。
  consolidation_order_pay_method_select: "consolidation_order_pay_method_select",
  // 集运订单确认支付点击（表格英文名：ConsolidationOrderConfirmPayClick）。
  consolidation_order_confirm_pay_click: "consolidation_order_confirm_pay_click",
  // 集运订单支付成功（表格英文名：ConsolidationOrderPaySuccess）。
  consolidation_order_pay_success: "consolidation_order_pay_success",
  // 集运订单支付失败（表格英文名：ConsolidationOrderPayFail）。
  consolidation_order_pay_fail: "consolidation_order_pay_fail",

  // 集运订单列表页访问（表格英文名：ConsolidationOrderListView）。
  consolidation_order_list_view: "consolidation_order_list_view",
  // 集运订单列表付款点击（表格英文名：ConsolidationPayClick）。
  consolidation_pay_click: "consolidation_pay_click",
  // 集运订单详情页访问（表格英文名：ConsolidationDetailView）。
  consolidation_detail_view: "consolidation_detail_view",
  // 集运订单详情取消点击（表格英文名：ConsolidationDetailCancelClick）。
  consolidation_detail_cancel_click: "consolidation_detail_cancel_click",
  // 集运订单详情付款点击（表格英文名：ConsolidationDetailPayClick）。
  consolidation_detail_pay_click: "consolidation_detail_pay_click",

  // 会员中心页访问（表格英文名：UserCenterView）。
  user_center_view: "user_center_view",
  // 我的订单入口点击（表格英文名：MyOrderEntryClick）。
  my_order_entry_click: "my_order_entry_click",
  // 我的仓库入口点击（表格英文名：MyWarehouseEntryClick）。
  my_warehouse_entry_click: "my_warehouse_entry_click",
  // 我的包裹入口点击（表格英文名：MyPackageEntryClick）。
  my_package_entry_click: "my_package_entry_click",
  // 专家代购订单入口点击（表格英文名：AgentOrderEntryClick）。
  agent_order_entry_click: "agent_order_entry_click",

  // 地址保存提交（表格英文名：AddressSaveSubmit）。
  address_save_submit: "address_save_submit",

  // 登录页访问（表格英文名：LoginPageView）。
  login_page_view: "login_page_view",
  // 账号密码登录点击（表格英文名：LoginWithPasswordClick）。
  login_with_password_click: "login_with_password_click",
  // 第三方登录点击（表格英文名：ThirdPartyLoginClick）。
  third_party_login_click: "third_party_login_click",
  // 邮箱验证码登录点击（表格英文名：LoginWithEmailCodeClick）。
  login_with_email_code_click: "login_with_email_code_click",
  // 注册页访问（表格英文名：RegisterPageView）。
  register_page_view: "register_page_view",
  // 注册提交点击（表格英文名：RegisterSubmitClick）。
  register_submit_click: "register_submit_click",
  // 第三方注册点击（表格英文名：ThirdPartyRegisterClick）。
  third_party_register_click: "third_party_register_click",

  // 以下为存量调用别名；仅替换最终事件名，不改原有调用链路。
  auth_login_attempt: "auth_login_attempt",
  auth_login_success: "auth_login_success",
  auth_login_fail: "auth_login_fail",
  auth_register_attempt: "auth_register_attempt",
  auth_register_success: "auth_register_success",
  auth_register_fail: "auth_register_fail",
  tb_goods_detail_view: "product_detail_view",
  tb_add_to_cart: "add_to_cart_click",
  tb_buy_now: "buy_now_click",
  tb_cart_checkout: "cart_checkout_click",
  tb_cart_delete: "cart_delete",
  tb_cart_select_item: "cart_select_item",
  tb_cart_select_all: "cart_select_all",
  tb_cart_select_store: "cart_select_store",
  alibaba_goods_detail_view: "product_detail_view",
  alibaba_add_to_cart: "add_to_cart_click",
  alibaba_buy_now: "buy_now_click",
  alibaba_cart_checkout: "cart_checkout_click",
  alibaba_cart_delete: "cart_delete",
  alibaba_cart_select_item: "cart_select_item",
  alibaba_cart_select_all: "cart_select_all",
  alibaba_cart_select_store: "cart_select_store",
  tb_order_create_start: "confirm_order_submit_click",
  tb_order_create_success: "create_order_success",
  tb_order_create_fail: "create_order_fail",
  tb_order_create_error: "create_order_fail",
  tb_payment_start: "domestic_order_confirm_pay_click",
  tb_payment_success: "domestic_order_pay_success",
  tb_payment_fail: "domestic_order_pay_fail",
  tb_payment_error: "domestic_order_pay_fail",
  alibaba_order_create_start: "confirm_order_submit_click",
  alibaba_order_create_success: "create_order_success",
  alibaba_order_create_fail: "create_order_fail",
  alibaba_payment_start: "domestic_order_confirm_pay_click",
  alibaba_payment_success: "domestic_order_pay_success",
  alibaba_payment_fail: "domestic_order_pay_fail",
  alibaba_payment_error: "domestic_order_pay_fail",
  expert_purchase_success: "expert_agent_submit_click",
};

const ROUTE_PAGE_CODE_MAP = {
  "/": "home",
  "/mall/home": "user_center",
  "/user-center/mall/home": "user_center",
  "/mall/cart": "cart_list",
  "/user-center/mall/cart": "cart_list",
  "/tb/goods": "product_detail",
  "/wd/goods": "product_detail",
  "/mall/tbDirectPaymentConfirm": "direct_payment_confirm",
  "/user-center/mall/tbDirectPaymentConfirm": "direct_payment_confirm",
  "/mall/tbPayOrder": "order_confirm",
  "/user-center/mall/tbPayOrder": "order_confirm",
  "/mall/platformAllOrderListV2": "order_list",
  "/user-center/mall/platformAllOrderListV2": "order_list",
  "/mall/orderList": "order_list",
  "/shop/expertPurchaseHome": "expert_purchase",
  "/user-center/mall/expertPurchaseHome": "expert_purchase",
};

// 继续复用现有路由页面上报，只把表格中的页面访问事件补到对应路由。
const ROUTE_VIEW_EVENT_CODE_MAP = {
  "/": "home_page_view",
  "/mall/home": "user_center_view",
  "/user-center/mall/home": "user_center_view",
  "/mall/cart": "cart_page_view",
  "/user-center/mall/cart": "cart_page_view",
  "/mall/expertPurchaseHome": "expert_agent_page_view",
  "/shop/expertPurchaseHome": "expert_agent_page_view",
  "/user-center/mall/expertPurchaseHome": "expert_agent_page_view",
  "/mall/confirmation1688Order": "confirm_order_page_view",
  "/mall/confirmationWeidianOrder": "confirm_order_page_view",
  "/mall/tbDirectPaymentConfirm": "confirm_order_page_view",
  "/user-center/mall/confirmation1688Order": "confirm_order_page_view",
  "/user-center/mall/confirmationWeidianOrder": "confirm_order_page_view",
  "/user-center/mall/tbDirectPaymentConfirm": "confirm_order_page_view",
  "/mall/Pay1688Order": "domestic_order_pay_page_view",
  "/mall/PayWeidianOrder": "domestic_order_pay_page_view",
  "/mall/tbPayOrder": "domestic_order_pay_page_view",
  "/user-center/mall/Pay1688Order": "domestic_order_pay_page_view",
  "/user-center/mall/PayWeidianOrder": "domestic_order_pay_page_view",
  "/user-center/mall/tbPayOrder": "domestic_order_pay_page_view",
  "/mall/platformAllOrderList": "domestic_order_list_view",
  "/mall/platformAllOrderListV2": "domestic_order_list_view",
  "/user-center/mall/platformAllOrderList": "domestic_order_list_view",
  "/user-center/mall/platformAllOrderListV2": "domestic_order_list_view",
  "/mall/allOrderDetail": "purchase_order_detail_view",
  "/mall/tbOrderDetail": "purchase_order_detail_view",
  "/mall/weidianOrderDetail": "purchase_order_detail_view",
  "/mall/unifiedOrderDetail": "purchase_order_detail_view",
  "/user-center/mall/allOrderDetail": "purchase_order_detail_view",
  "/user-center/mall/tbOrderDetail": "purchase_order_detail_view",
  "/user-center/mall/weidianOrderDetail": "purchase_order_detail_view",
  "/user-center/mall/unifiedOrderDetail": "purchase_order_detail_view",
  "/collection/myWarehouse": "warehouse_page_view",
  "/user-center/collection/myWarehouse": "warehouse_page_view",
  "/collection/myPackage": "consolidation_order_list_view",
  "/user-center/collection/myPackage": "consolidation_order_list_view",
  "/collection/order": "consolidation_order_list_view",
  "/user-center/collection/order": "consolidation_order_list_view",
  "/login": "login_page_view",
  "/user-center/login": "login_page_view",
};

let initialized = false;
let queueCache = null;
let flushTimer = null;
let flushing = false;
let online = true;
let networkType = "unknown";
let contextProvider = null;
let lastScreenViewSignature = "";
let lastScreenViewAt = 0;
const requestFailureDedupMap = new Map();

function now() {
  return Date.now();
}

function getWindow() {
  return typeof window !== "undefined" ? window : null;
}

function getStorage() {
  return getWindow()?.localStorage || null;
}

function randomId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function formatDate(date) {
  const pad = n => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function safeJsonParse(value, fallback) {
  if (!value) return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function safeJsonStringify(value, fallback = {}) {
  try {
    return JSON.stringify(value);
  } catch {
    return JSON.stringify(fallback);
  }
}

function getByPath(source, path) {
  if (!source || !path) return undefined;
  return String(path).split(".").reduce((current, key) => {
    if (current === undefined || current === null) return undefined;
    return current[key];
  }, source);
}

function toBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

function normalizeMallApiBaseUrl(rawUrl = "") {
  let nextUrl = String(rawUrl || "").trim();
  if (!nextUrl) return "";
  nextUrl = nextUrl.replace(/\/+$/, "").replace(/\/mall$/i, "");
  const win = getWindow();
  if (win) {
    if (win.location.protocol === "http:" && nextUrl.startsWith("https://")) {
      nextUrl = nextUrl.replace("https://", "http://");
    } else if (win.location.protocol === "https:" && nextUrl.startsWith("http://")) {
      nextUrl = nextUrl.replace("http://", "https://");
    }
  }
  return nextUrl.replace(/\/+$/, "");
}

function parseOtherJson(storeInfo = {}) {
  const parse = value => {
    if (!value) return {};
    if (typeof value === "object") return value;
    return safeJsonParse(value, {});
  };
  const offiaccountFollowData = parse(storeInfo.offiaccountFollowData || storeInfo.offiaccountFollowUrl);
  const candidates = [
    storeInfo?.offiaccountFollowData?.otherJson,
    storeInfo?.storePageInfo?.otherJson,
    storeInfo?.otherJson,
    offiaccountFollowData?.otherJson,
    storeInfo?.storePageInfo,
  ];
  for (const candidate of candidates) {
    const parsed = parse(candidate);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length) {
      return parsed;
    }
  }
  return {};
}

function getStoredStoreInformation() {
  const storage = getStorage();
  if (!storage) return {};
  const storeInfo = safeJsonParse(storage.getItem("StoreInformation"), {});
  if (typeof storeInfo?.offiaccountFollowUrl === "string" && !storeInfo.offiaccountFollowData) {
    storeInfo.offiaccountFollowData = safeJsonParse(storeInfo.offiaccountFollowUrl, {});
  }
  if (typeof storeInfo?.offiaccountFollowData?.otherJson === "string") {
    storeInfo.offiaccountFollowData.otherJson = safeJsonParse(storeInfo.offiaccountFollowData.otherJson, {});
  }
  return storeInfo || {};
}

function getContext() {
  if (typeof contextProvider === "function") {
    try {
      return contextProvider() || {};
    } catch {
      return {};
    }
  }
  return {};
}

export function setMallEventLogContextProvider(provider) {
  contextProvider = typeof provider === "function" ? provider : null;
}

function getStoreInfo() {
  const context = getContext();
  const explicit = context.storeInfo || context.storeInfoData;
  if (explicit && typeof explicit === "object" && Object.keys(explicit).length) {
    return explicit;
  }
  return getStoredStoreInformation();
}

function getSupportEventLog(app) {
  const candidates = [
    getByPath(app, "shopInfor.offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(app, "storeInfoData.offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "offiaccountFollowData.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "storePageInfo.otherJson.supportEventLog"),
    getByPath(getStoreInfo(), "otherJson.supportEventLog"),
    parseOtherJson(getStoreInfo()).supportEventLog,
  ];
  return candidates.some(toBoolean);
}

function getApiUrl() {
  return "/apph5/customer-api/admin/mallEventLog/batchSave";
}

function getAuthToken() {
  const storage = getStorage();
  const context = getContext();
  return (
    context.authToken ||
    context.mallToken ||
    storage?.getItem("sassmall-app-token") ||
    storage?.getItem("mall_app_user_key") ||
    storage?.getItem("sj_mall_admin_token") ||
    storage?.getItem("token") ||
    storage?.getItem("authorization") ||
    ""
  );
}

function getDeviceId() {
  const storage = getStorage();
  if (!storage) return randomId("d");
  const cached = storage.getItem(DEVICE_ID_KEY);
  if (cached) return cached;
  const value = randomId("d");
  storage.setItem(DEVICE_ID_KEY, value);
  return value;
}

function getSessionId() {
  const storage = getStorage();
  if (!storage) return randomId("s");
  const cached = safeJsonParse(storage.getItem(SESSION_KEY), {});
  const isExpired = !cached.id || !cached.updatedAt || now() - cached.updatedAt > SESSION_TTL;
  const next = {
    id: isExpired ? randomId("s") : cached.id,
    updatedAt: now(),
  };
  storage.setItem(SESSION_KEY, JSON.stringify(next));
  return next.id || randomId("s");
}

function parseUrlParams() {
  const win = getWindow();
  if (!win) return {};
  const full = `${win.location.search || ""}&${(win.location.hash || "").split("?")[1] || ""}`;
  const params = new URLSearchParams(full.replace(/^&/, ""));
  const result = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

function getFirstTouch() {
  const storage = getStorage();
  if (!storage) return {};
  const cached = safeJsonParse(storage.getItem(FIRST_TOUCH_KEY), {});
  if (Object.keys(cached).length) return cached;
  const params = parseUrlParams();
  const touch = {
    channel: params.channel || params.utm_source || params.source || params.scene || params.shareCode || params.inviteCode || "",
    utm_source: params.utm_source || "",
    utm_campaign: params.utm_campaign || "",
    source: params.source || "",
    scene: params.scene || "",
  };
  storage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  return touch;
}

function getUserId(app) {
  const context = getContext();
  const candidates = [
    context.userId,
    getByPath(app, "mallUserInfo.id"),
    getByPath(context, "mallUserInfo.id"),
    getByPath(context, "userInfo.mallUserInfo.id"),
    getByPath(context, "userInfo.id"),
    getByPath(context, "user.id"),
  ];
  const found = candidates.find(value => value !== null && value !== undefined && value !== "");
  return found || null;
}

export function getMallEventUserContext(app) {
  const context = getContext();
  const userId = getUserId(app);
  const memberCodeCandidates = [
    context.memberCode,
    getByPath(app, "mallUserInfo.memberCode"),
    getByPath(app, "userInfo.memberCode"),
    getByPath(context, "mallUserInfo.memberCode"),
    getByPath(context, "userInfo.mallUserInfo.memberCode"),
    getByPath(context, "userInfo.memberCode"),
  ];
  const memberCode = memberCodeCandidates.find(
    value => value !== null && value !== undefined && value !== "",
  );

  return {
    userId,
    user_id: userId,
    memberCode: memberCode ? String(memberCode) : "",
    member_code: memberCode ? String(memberCode) : "",
    loggedIn: Boolean(userId || memberCode || getAuthToken()),
    is_logged_in: Boolean(userId || memberCode || getAuthToken()),
  };
}

function withCurrentAnalyticsContext(source = {}, app) {
  const normalizedSource = source && typeof source === "object" && !Array.isArray(source)
    ? source
    : {};
  const deviceContext = getWebAnalyticsDeviceContext(getWindow());
  const currentMemberCode = getMallEventUserContext(app).member_code;
  const memberCode = currentMemberCode
    || pickValue(normalizedSource, ["member_code", "memberCode"])
    || "";
  const trackingChannel = getMallTrackingChannel();
  return {
    ...normalizedSource,
    ...deviceContext,
    member_code: String(memberCode),
    ...(trackingChannel ? { mall_tracking_channel: trackingChannel } : {}),
  };
}

function pickValue(source = {}, keys = []) {
  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function toNullableNumber(value) {
  if (value === undefined || value === null || value === "") return null;
  const normalized = Array.isArray(value) ? value[0] : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNullableString(value) {
  if (value === undefined || value === null || value === "") return null;
  if (Array.isArray(value)) return value.length ? String(value[0]) : null;
  return String(value);
}

function getNestedItemValue(source = {}, keys = []) {
  const items = Array.isArray(source.items) ? source.items : [];
  for (const item of items) {
    const value = pickValue(item || {}, keys);
    if (value !== undefined) return value;
  }
  return undefined;
}

function getStandardParamCandidate(key, source = {}, common = {}) {
  const aliases = {
    userId: ["userId", "user_id"],
    sessionId: ["sessionId", "session_id"],
    utmSource: ["utmSource", "utm_source", "channel"],
    device: ["device", "deviceType", "device_type"],
    productId: ["productId", "productSpuId", "itemId", "item_id", "offerId"],
    productName: ["productName", "title", "itemTitle", "item_name"],
    spec: ["spec", "specsInfo", "specification", "skuName", "sku_name"],
    sourcePlatform: ["sourcePlatform", "platform", "platformType", "platform_type", "orderSource"],
    fromPage: ["fromPage", "from_page", "referer", "referrer"],
    shopId: ["shopId", "shop_id", "sellerId", "sellerOpenId"],
    itemCount: ["itemCount", "productCount", "packageCount", "count", "quantity"],
    checkedCount: ["checkedCount", "selectedCount", "selectCount"],
    totalAmount: ["totalAmount", "totalPrice", "payAmount", "payTotalAmount", "orderAmount"],
    goodsAmount: ["goodsAmount", "goodsTotalAmount", "totalPrice", "subtotal", "sumPaymentNoCarriage"],
    discountAmount: ["discountAmount", "discount", "couponAmount", "couponDiscount"],
    payAmount: ["payAmount", "payTotalAmount", "totalAmount", "totalPrice", "orderAmount", "custPayMoney"],
    productUrl: ["productUrl", "itemLink", "productLink", "url"],
    remark: ["remark", "orderRemark", "notes"],
    orderId: ["orderId", "order_id", "purchaseId", "purchase_id", "outerOrderId", "orderCode"],
    orderType: ["orderType", "sourcePlatform", "platform", "orderSource"],
    payMethodId: ["payMethodId", "paymentMethodId", "methodId", "payType", "paymentMethod"],
    payMethodName: ["payMethodName", "paymentMethodName", "methodName", "paymentMethod", "payMethod"],
    payMethod: ["payMethod", "paymentMethod", "paymentMethodName", "payTypeName", "payType"],
    failReason: ["failReason", "errorMessage", "error_message", "message", "msg"],
    errorCode: ["errorCode", "error_code", "code", "statusCode"],
    currentTab: ["currentTab", "displayStatus", "tab", "type", "statusGroupCode"],
    orderStatus: ["orderStatus", "status", "displayStatus", "statusName"],
    cancelReason: ["cancelReason", "reason", "failReason", "message"],
    orderAmount: ["orderAmount", "payAmount", "payTotalAmount", "totalAmount", "totalPrice"],
    packageIds: ["packageIds", "packageIdList", "ids"],
    packageCount: ["packageCount", "itemCount", "selectedCount", "count"],
    pendingCount: ["pendingCount", "waitingCount", "toBeOrderedCount"],
    "Unstored shipmentCount": ["Unstored shipmentCount", "unstoredShipmentCount", "unStoredCount", "shippingCount"],
    totalWeight: ["totalWeight", "weight"],
    totalVolume: ["totalVolume", "volume"],
    destination: ["destination", "country", "warehouseName", "address"],
    shippingMethod: ["shippingMethod", "transportMethod", "deliveryMethod", "lineName"],
    estimatedFee: ["estimatedFee", "shippingFee", "freight", "totalAmount", "payAmount"],
    consolidationId: ["consolidationId", "orderId", "id", "orderCode"],
    shippingFee: ["shippingFee", "freight", "carriage", "payAmount", "totalAmount"],
    status: ["status", "orderStatus", "statusName"],
    weight: ["weight", "totalWeight"],
    shippingCount: ["shippingCount", "shippedCount", "packageCount"],
    addressId: ["addressId", "id"],
    region: ["region", "provinceCityDistrict"],
    country: ["country", "countryName"],
    isDefault: ["isDefault", "defaultState"],
    redirectUrl: ["redirectUrl", "redirect", "redirect_uri"],
    accountType: ["accountType", "loginType", "method"],
    emailDomain: ["emailDomain", "domain"],
    inviteCode: ["inviteCode", "invite_code"],
    registerType: ["registerType", "method", "accountType"],
    rank: ["rank", "index", "position"],
    price: ["price", "unitPrice", "itemPrice"],
    quantity: ["quantity", "count", "itemCount"],
    platformName: ["platformName", "platform", "name"],
    source: ["source", "recommendSource", "recommend_source"],
    "Page source": ["Page source", "pageSource", "fromPage", "source"],
  };
  const direct = pickValue(source, aliases[key] || [key]);
  if (direct !== undefined) return direct;

  if (key === "productId") return getNestedItemValue(source, ["productId", "itemId", "offerId"]);
  if (key === "productName") return getNestedItemValue(source, ["productName", "title", "itemTitle"]);
  if (key === "spec") return getNestedItemValue(source, ["spec", "specsInfo", "skuName"]);
  if (key === "productUrl") return getNestedItemValue(source, ["productUrl", "itemLink", "url", "itemId"]);
  if (key === "Product ID+Product Link") {
    const items = Array.isArray(source.items) ? source.items : [];
    if (items.length) {
      return items
        .map(item => pickValue(item || {}, ["productUrl", "itemLink", "productId", "itemId", "offerId"]))
        .filter(value => value !== undefined)
        .join(",");
    }
    return pickValue(source, ["productUrl", "itemLink", "productId", "itemId", "offerId"]);
  }
  if (key === "Total number of products") {
    const itemCount = pickValue(source, ["itemCount", "productCount", "quantity"]);
    if (itemCount !== undefined) return itemCount;
    return Array.isArray(source.items) ? source.items.length : undefined;
  }
  if (key === "Source platform (Taobao: N; 1688: M)") {
    const explicit = pickValue(source, [key, "sourcePlatformSummary"]);
    if (explicit !== undefined) return explicit;
    const count = Number(getStandardParamCandidate("Total number of products", source, common) || 0);
    const platform = String(pickValue(source, ["sourcePlatform", "platform", "orderSource"]) || "").toLowerCase();
    const is1688 = platform.includes("1688") || platform.includes("alibaba") || platform === "2";
    return `Taobao: ${is1688 ? 0 : count}; 1688: ${is1688 ? count : 0}`;
  }
  if (key === "isSuccess") {
    const result = pickValue(source, ["isSuccess", "success", "result"]);
    if (result !== undefined) return result;
    return !String(common.eventCode || "").endsWith("_fail");
  }
  return common[key];
}

function coerceStandardParam(value, type) {
  if (type === "number") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (type === "boolean") {
    if (value === undefined || value === null || value === "") return false;
    if (typeof value === "string") return !["false", "0", "failure", "fail"].includes(value.trim().toLowerCase());
    return Boolean(value);
  }
  if (Array.isArray(value)) return value.join(",");
  return value === undefined || value === null ? "" : String(value);
}

function normalizeP0EventSource(eventCode, source = {}, common = {}) {
  const schema = P0_EVENT_PARAM_SCHEMA[eventCode];
  if (!schema) return source;
  const normalized = { ...source };
  Object.entries(schema).forEach(([key, type]) => {
    normalized[key] = coerceStandardParam(
      getStandardParamCandidate(key, normalized, { ...common, eventCode }),
      type,
    );
  });
  return normalized;
}

// The first-party log keeps the complete source payload. GA receives only
// report-safe fields plus standard ecommerce identifiers; free-form text and
// user-entered URLs stay first-party because they can contain personal data.
const FIRST_PARTY_ONLY_GA_PARAMS = new Set([
  "cancelReason",
  "error_message",
  "failReason",
  "message",
  "msg",
  "productUrl",
  "redirectUrl",
  "remark",
]);

function buildGoogleAnalyticsBusinessParams(eventCode, source = {}, common = {}) {
  const schema = P0_EVENT_PARAM_SCHEMA[eventCode] || {};
  const normalized = { ...source };

  Object.keys(schema).forEach((key) => {
    const candidate = getStandardParamCandidate(key, source, { ...common, eventCode });
    if (candidate === undefined || candidate === null || candidate === "") return;
    normalized[key] = coerceStandardParam(candidate, schema[key]);
  });

  FIRST_PARTY_ONLY_GA_PARAMS.forEach(key => delete normalized[key]);

  const transactionId = pickValue(normalized, [
    "transaction_id",
    "orderId",
    "order_id",
    "orderCode",
    "outerOrderId",
  ]);
  if (transactionId !== undefined) normalized.transaction_id = String(transactionId);

  if (!Array.isArray(normalized.items) || !normalized.items.length) {
    const itemId = pickValue(normalized, ["productId", "itemId", "item_id", "offerId"]);
    const itemName = pickValue(normalized, ["productName", "itemName", "item_name", "title"]);
    if (itemId !== undefined || itemName !== undefined) {
      normalized.items = [{
        ...(itemId !== undefined ? { item_id: String(itemId) } : {}),
        ...(itemName !== undefined ? { item_name: String(itemName) } : {}),
        ...(toNullableNumber(normalized.price) !== null ? { price: Number(normalized.price) } : {}),
        ...(toNullableNumber(normalized.quantity) !== null ? { quantity: Number(normalized.quantity) } : {}),
      }];
    }
  }

  return normalized;
}

function resolveOrderSource(source = {}, screenName = "") {
  const explicit = toNullableNumber(pickValue(source, ["orderSource", "order_source", "sourcePlatform"]));
  if (explicit) return explicit;
  const platform = String(pickValue(source, ["platform", "platformType", "platform_type", "origin"]) || "").toLowerCase();
  if (platform.includes("1688") || platform.includes("alibaba") || platform === "2") return 2;
  if (platform.includes("tb") || platform.includes("taobao") || platform === "1") return 1;
  if (String(screenName).toLowerCase().includes("alibaba")) return 2;
  return 1;
}

function resolvePageCode(screenName = "", source = {}, fallback = "") {
  const explicit = toNullableString(pickValue(source, ["pageCode", "page_code", "screen_name", "screenName"]));
  if (explicit) return explicit;
  const path = String(screenName || getWindow()?.location?.pathname || "");
  const matchedKey = Object.keys(ROUTE_PAGE_CODE_MAP)
    .sort((a, b) => b.length - a.length)
    .find(key => path === key || path.startsWith(`${key}/`));
  return matchedKey ? ROUTE_PAGE_CODE_MAP[matchedKey] : (fallback || screenName || "unknown_page");
}

function resolveRouteViewEventCode(screenName = "") {
  const path = String(screenName || getWindow()?.location?.pathname || "");
  const matchedKey = Object.keys(ROUTE_VIEW_EVENT_CODE_MAP)
    .sort((a, b) => b.length - a.length)
    .find((key) => {
      if (key === "/") return path === "/";
      return path === key || path.startsWith(`${key}/`);
    });
  return matchedKey ? ROUTE_VIEW_EVENT_CODE_MAP[matchedKey] : "page_view";
}

function getCurrentChannel(source = {}) {
  const explicit = toNullableString(pickValue(source, ["channel", "utm_source", "source", "scene"]));
  if (explicit) return explicit;
  const touch = getFirstTouch();
  return touch.channel || touch.utm_source || touch.source || touch.scene || "direct";
}

function getWebNetworkType() {
  const win = getWindow();
  if (!win) return "unknown";
  const connection = win.navigator?.connection || win.navigator?.mozConnection || win.navigator?.webkitConnection;
  return connection?.effectiveType || connection?.type || (win.navigator?.onLine === false ? "offline" : "online");
}

function buildExtJson(base = {}, source = {}) {
  const memberCode = toNullableString(source.member_code) || "";
  const deviceType = toNullableString(source.device_type) || "unknown";
  const osType = toNullableString(source.os_type) || "other";
  const osVersion = toNullableString(source.os_version) || "";
  const trackingChannel = toNullableString(source.mall_tracking_channel) || "";
  return safeJsonStringify({
    ...getFirstTouch(),
    networkType,
    mall_tracking_channel: trackingChannel,
    member_code: memberCode,
    device_type: deviceType,
    os_type: osType,
    os_version: osVersion,
    originalEventName: base.originalEventName,
    screenName: base.screenName,
    routePath: base.routePath,
    fromRouteName: base.fromRouteName,
    fromRoutePath: base.fromRoutePath,
    source,
  }, {
    mall_tracking_channel: trackingChannel,
    member_code: memberCode,
    device_type: deviceType,
    os_type: osType,
    os_version: osVersion,
    originalEventName: base.originalEventName,
    screenName: base.screenName,
    routePath: base.routePath,
  });
}

function buildPayload(eventCode, source = {}, meta = {}) {
  const userContext = getMallEventUserContext(meta.app);
  const sessionId = getSessionId();
  const firstTouch = getFirstTouch();
  const analyticsSource = withCurrentAnalyticsContext(source, meta.app);
  const eventSource = normalizeP0EventSource(eventCode, analyticsSource, {
    userId: userContext.userId,
    sessionId,
    utmSource: firstTouch.utm_source || firstTouch.channel || getCurrentChannel(analyticsSource),
    device: analyticsSource.device_type || analyticsSource.device || "unknown",
    fromPage: meta.fromRoutePath || meta.fromRouteName || "",
  });
  const screenName = toNullableString(meta.screenName) || undefined;
  const productSpuId = toNullableNumber(pickValue(eventSource, ["productSpuId", "product_spu_id", "itemId", "item_id"]));
  const productSkuId = toNullableNumber(pickValue(eventSource, ["productSkuId", "product_sku_id", "itemSkuId", "item_sku_id", "skuId", "sku_id"]));
  const rawOrderId =
    pickValue(eventSource, ["orderId", "order_id", "purchaseId", "purchase_id"]) ||
    pickValue(eventSource, ["orderIds", "order_ids"]) ||
    pickValue(eventSource, ["purchase_ids", "purchaseIds"]);
  const orderId = Array.isArray(rawOrderId)
    ? rawOrderId[0]
    : typeof rawOrderId === "string" && rawOrderId.includes(",")
      ? rawOrderId.split(",")[0]
      : rawOrderId;

  return {
    eventCode,
    eventTime: formatDate(new Date()),
    userId: userContext.userId,
    deviceId: getDeviceId(),
    sessionId,
    terminal: "web",
    channel: getCurrentChannel(eventSource),
    pageCode: resolvePageCode(screenName, eventSource, meta.pageCode),
    productSpuId,
    productSkuId,
    orderId: toNullableString(orderId),
    orderSource: resolveOrderSource(eventSource, screenName),
    eventTraceId: toNullableString(eventSource.event_trace_id),
    extJson: buildExtJson(meta, eventSource),
  };
}

function pruneQueue(queue) {
  const expireBefore = now() - MAX_QUEUE_AGE_MS;
  return queue.filter(item => item.createdAt >= expireBefore);
}

function readQueue() {
  if (queueCache) return queueCache;
  queueCache = pruneQueue(safeJsonParse(getStorage()?.getItem(QUEUE_KEY), []));
  return queueCache;
}

function writeQueue(queue) {
  queueCache = pruneQueue(queue).slice(-MAX_QUEUE_SIZE);
  getStorage()?.setItem(QUEUE_KEY, JSON.stringify(queueCache));
}

function scheduleFlush(delay = 2000) {
  if (!getWindow()) return;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushMallEventLogQueue().catch(error => {
      if (process.env.NODE_ENV === "development") {
        console.warn("[mall-event-log] flush failed", error);
      }
    });
  }, delay);
}

function enqueue(payload, eventTraceId = "") {
  const queue = readQueue();
  queue.push({
    id: eventTraceId || randomId("evt"),
    payload,
    retryCount: 0,
    nextRetryAt: now(),
    createdAt: now(),
  });
  writeQueue(queue);
  scheduleFlush(800);
}

function bumpRetry(queue, ids) {
  const next = queue.map((item) => {
    if (!ids.includes(item.id)) return item;
    const retryCount = item.retryCount + 1;
    const retryDelay = Math.min(BASE_RETRY_MS * Math.pow(2, retryCount - 1), MAX_RETRY_MS);
    return {
      ...item,
      retryCount,
      nextRetryAt: now() + retryDelay,
    };
  });
  writeQueue(next);
  scheduleFlush(BASE_RETRY_MS);
}

async function sendBatch(payloads, keepalive = false) {
  const headers = {
    "Content-Type": "application/json",
  };
  const token = getAuthToken();
  if (token) {
    headers.authorization = /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
  }
  const tenantDomain = String(
    getStorage()?.getItem("sassmall-tenant-domain") || getWindow()?.location?.hostname || "",
  ).trim();
  if (tenantDomain) headers["X-Tenant-Domain"] = tenantDomain;

  const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS) : null;
  try {
    const response = await fetch(getApiUrl(), {
      method: "POST",
      headers,
      body: JSON.stringify(payloads),
      keepalive,
      signal: controller?.signal,
    });
    if (!response.ok) {
      throw new Error(`mall event upload failed: ${response.status}`);
    }
    const data = await response.json().catch(() => ({ success: true }));
    if (data && data.success === false) {
      throw new Error(data.msg || "mall event upload failed");
    }
    return data;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function flushMallEventLogQueue(options = {}) {
  if (!getSupportEventLog() || flushing || !online) return;
  flushing = true;
  try {
    const queue = readQueue();
    const readyItems = queue.filter(item => item.nextRetryAt <= now()).slice(0, MAX_BATCH_SIZE);
    if (!readyItems.length) return;
    await sendBatch(readyItems.map((item) => {
      const { event_trace_id: legacyEventTraceId, ...payload } = item.payload;
      return {
        ...payload,
        eventTraceId: payload.eventTraceId || legacyEventTraceId || item.id,
      };
    }), !!options.keepalive);
    const sentIds = readyItems.map(item => item.id);
    writeQueue(queue.filter(item => !sentIds.includes(item.id)));
    if (readQueue().some(item => item.nextRetryAt <= now())) {
      scheduleFlush(500);
    }
  } catch (error) {
    bumpRetry(readQueue(), readQueue().filter(item => item.nextRetryAt <= now()).slice(0, MAX_BATCH_SIZE).map(item => item.id));
    throw error;
  } finally {
    flushing = false;
  }
}

function setLastRoute(route) {
  getStorage()?.setItem(LAST_ROUTE_KEY, JSON.stringify(route));
}

function getLastRoute() {
  return safeJsonParse(getStorage()?.getItem(LAST_ROUTE_KEY), {});
}

function setupWebLifecycle() {
  const win = getWindow();
  if (!win) return;
  win.addEventListener("online", () => {
    online = true;
    networkType = getWebNetworkType();
    scheduleFlush(200);
  });
  win.addEventListener("offline", () => {
    online = false;
    networkType = "offline";
  });
  win.addEventListener("pagehide", () => {
    flushMallEventLogQueue({ keepalive: true }).catch(() => undefined);
  });
  win.document?.addEventListener("visibilitychange", () => {
    if (win.document.visibilityState === "hidden") {
      flushMallEventLogQueue({ keepalive: true }).catch(() => undefined);
    }
  });
}

export function initializeMallEventLog() {

  if (initialized || !getSupportEventLog()) return;
  initialized = true;
  const win = getWindow();
  online = !win || win.navigator?.onLine !== false;
  networkType = getWebNetworkType();
  getDeviceId();
  getSessionId();
  getFirstTouch();
  setupWebLifecycle();
  if (readQueue().length) scheduleFlush(1200);
}

function sanitizeRequestUrl(url = "") {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    if (/^https?:\/\//i.test(raw)) {
      const parsed = new URL(raw);
      return `${parsed.pathname || ""}${parsed.search || ""}`.slice(0, 240);
    }
  } catch {}
  return raw.slice(0, 240);
}

function cleanupRequestFailureDedupMap() {
  const expireBefore = now() - REQUEST_FAILURE_DEDUP_WINDOW_MS;
  requestFailureDedupMap.forEach((value, key) => {
    if (value < expireBefore) requestFailureDedupMap.delete(key);
  });
}

export function reportMallApiRequestFailure(input = {}) {
  if (!getSupportEventLog(input.app)) return;
  initializeMallEventLog();
  const routeMeta = getLastRoute();
  const requestUrl = sanitizeRequestUrl(input.url);
  if (!requestUrl || REQUEST_FAILURE_IGNORE_PATTERNS.some(pattern => requestUrl.includes(pattern))) {
    return;
  }
  const message = String(input.message?.message || input.message || "").slice(0, 220);
  const requestMethod = (toNullableString(input.method) || "GET").toUpperCase();
  const status = toNullableNumber(input.status);
  const stage = toNullableString(input.stage) || "transport";
  const signature = [requestMethod, requestUrl, status ?? "na", stage, message || ""].join("::");
  const lastReportedAt = requestFailureDedupMap.get(signature) || 0;
  if (now() - lastReportedAt < REQUEST_FAILURE_DEDUP_WINDOW_MS) return;
  requestFailureDedupMap.set(signature, now());
  cleanupRequestFailureDedupMap();

  const eventTraceId = randomId("evt");
  enqueue(buildPayload(API_REQUEST_FAIL_EVENT_CODE, {
    event_trace_id: eventTraceId,
    request_url: requestUrl,
    request_method: requestMethod,
    http_status: status ?? undefined,
    duration_ms: toNullableNumber(input.durationMs) ?? undefined,
    request_stage: stage,
    error_message: message,
    network_type: networkType,
    ...(input.ext || {}),
  }, {
    app: input.app,
    originalEventName: "mall_api_request_fail",
    screenName: input.screenName || routeMeta.screenName,
    routePath: input.routePath || routeMeta.routePath,
    fromRouteName: routeMeta.fromRouteName,
    fromRoutePath: routeMeta.fromRoutePath,
  }), eventTraceId);
}

export function mallAnalyticsLogEvent(eventName, params = {}, app, googleAnalyticsParams = {}) {
  const eventCode = EVENT_CODE_MAP[eventName];
  if (!eventCode) return false;
  // This opaque ID is shared by GA and the first-party event log. It lets an
  // operator find the complete first-party payload without sending personal or
  // high-cardinality business data as GA custom dimensions.
  const eventTraceId = randomId("evt");
  const eventParams = withCurrentAnalyticsContext({
    ...params,
    event_trace_id: eventTraceId,
  }, app);
  const routeMeta = getLastRoute();
  const userContext = getMallEventUserContext(app);
  const firstTouch = getFirstTouch();
  const googleAnalyticsSource = withCurrentAnalyticsContext({
    ...eventParams,
    business_event_code: eventCode,
    ...(googleAnalyticsParams || {}),
  }, app);
  const googleAnalyticsTracked = trackGoogleAnalyticsEvent(eventName, buildGoogleAnalyticsBusinessParams(
    eventCode,
    googleAnalyticsSource,
    {
      userId: userContext.userId,
      sessionId: getSessionId(),
      utmSource: firstTouch.utm_source || firstTouch.channel || getCurrentChannel(eventParams),
      device: eventParams.device_type || eventParams.device || "unknown",
      fromPage: routeMeta.fromRoutePath || routeMeta.fromRouteName || "",
    },
  ));
  const pixelTracked = trackPixelAnalyticsEvent(eventName, eventParams);
  if (!getSupportEventLog(app)) return googleAnalyticsTracked || pixelTracked;
  initializeMallEventLog();
  // console.log('mallAnalyticsLogEvent', eventName, params)
  enqueue(buildPayload(eventCode, eventParams, {
    app,
    originalEventName: eventName,
    screenName: pickValue(params || {}, ["screenName", "screen_name"]) || routeMeta.screenName,
    routePath: routeMeta.routePath,
    fromRouteName: routeMeta.fromRouteName,
    fromRoutePath: routeMeta.fromRoutePath,
  }), eventTraceId);
  return googleAnalyticsTracked || pixelTracked;
}

export function mallAnalyticsLogScreenView(screenName, routeParams = {}, fromRouteName, fromRoutePath, app) {
  if (!screenName) return;
  const routePath = String(routeParams?.fullPath || routeParams?.path || screenName || "");
  const screenViewSignature = `${screenName}::${routePath}`;
  const currentTime = now();
  if (
    screenViewSignature === lastScreenViewSignature
    && currentTime - lastScreenViewAt < SCREEN_VIEW_DEDUP_WINDOW_MS
  ) {
    return;
  }
  lastScreenViewSignature = screenViewSignature;
  lastScreenViewAt = currentTime;
  const eventTraceId = randomId("evt");
  const eventParams = withCurrentAnalyticsContext({
    ...routeParams,
    event_trace_id: eventTraceId,
    business_event_code: resolveRouteViewEventCode(screenName),
  }, app);
  trackGoogleAnalyticsPageView(screenName, eventParams, fromRouteName, fromRoutePath);
  trackPixelPageView(screenName, eventParams);
  if (!getSupportEventLog(app)) return;
  initializeMallEventLog();
  setLastRoute({
    screenName,
    routePath,
    fromRouteName,
    fromRoutePath,
    updatedAt: now(),
  });
  const normalizedPath = String(routeParams?.path || screenName || "");
  if (COMPONENT_MANAGED_VIEW_ROUTE_PREFIXES.some(prefix => normalizedPath.startsWith(prefix))) {
    return;
  }
  const pageCode = resolvePageCode(screenName, routeParams || {});
  const eventCode = resolveRouteViewEventCode(screenName);
  enqueue(buildPayload(eventCode, eventParams, {
    app,
    originalEventName: "screen_view",
    screenName,
    routePath,
    fromRouteName,
    fromRoutePath,
    pageCode,
  }), eventTraceId);
}

export function createMallEventLogContextFromNuxtStore(store, currentServer = "") {
  const state = store?.$state || store || {};
  const storeInfoMap = state.storeInfoDataMap || {};
  const storeInfo = storeInfoMap[currentServer] || Object.values(storeInfoMap)[0] || getStoredStoreInformation();
  return {
    storeInfo,
    mallUserInfo: state.mallUserInfo,
    userInfo: state.userInfo,
    userId: state.mallUserInfo?.id || state.userInfo?.id,
    mallApiUrl: parseOtherJson(storeInfo).mallApiUrl,
  };
}
